import { Link as ScrollLink } from "react-scroll";
import { motion } from "framer-motion";
import { useState } from "react";

function Home() {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <section className="min-h-screen flex items-center justify-center pt-16 relative overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 bg-gradient-to-br from-light via-gray-50 to-gray-100 z-0"></div>
      <div className="absolute inset-0 bg-grid-pattern opacity-5 z-0"></div>

      {/* Decorative elements */}
      <div className="absolute top-40 right-10 w-72 h-72 bg-primary/5 rounded-full filter blur-3xl opacity-70 z-0 animate-blob"></div>
      <div className="absolute bottom-40 left-10 w-72 h-72 bg-secondary/5 rounded-full filter blur-3xl opacity-70 z-0 animate-blob animation-delay-4000"></div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-12">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full md:w-1/2"
          >
            <span className="text-primary font-medium relative inline-block">
              <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-primary to-secondary rounded-full transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100"></span>
              Hello, I'm
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-dark mt-2 mb-4">
              Abdullah Alioğlu
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              React Developer
            </h2>
            <p className="text-gray-700 text-lg mb-8 max-w-lg">
              I'm a passionate full-stack developer specializing in React and
              Next.js. I build responsive, performant web applications with
              efficient server-side rendering and optimized client experiences.
              My focus is on creating scalable solutions with clean architecture
              and seamless user interfaces.
            </p>
            <div className="flex flex-wrap gap-4">
              <ScrollLink
                to="projects"
                spy={true}
                smooth={true}
                offset={-70}
                duration={800}
                className="bg-gradient-to-r from-primary to-secondary text-white font-medium py-3 px-6 rounded-lg hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:text-white"
              >
                View Projects
              </ScrollLink>
              <ScrollLink
                to="contact"
                spy={true}
                smooth={true}
                offset={-70}
                duration={1000}
                className="border border-primary text-primary font-medium py-3 px-6 rounded-lg hover:bg-primary hover:text-white transition-all duration-300 transform hover:-translate-y-1"
              >
                Contact Me
              </ScrollLink>
              <a
                href="https://opsgvvtoutcxldtxqkls.supabase.co/storage/v1/object/public/portfolio//Abdullah-Alioglu-FrontEnd-CV.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center bg-white text-dark border border-gray-300 font-medium py-3 px-6 rounded-lg hover:shadow-lg hover:border-primary transition-all duration-300 transform hover:-translate-y-1 group"
              >
                <span className="mr-2">Download CV</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 group-hover:text-primary transition-colors duration-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                  />
                </svg>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-full md:w-1/2 flex justify-center"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className="relative w-64 h-64 md:w-80 md:h-80 group">
              {/* Background glow effect */}
              <motion.div
                className="absolute -inset-0.5 bg-gradient-to-r from-primary to-secondary rounded-full opacity-75 blur-md group-hover:opacity-100 transition duration-1000"
                animate={{
                  scale: isHovering ? [1, 1.05, 1] : 1,
                }}
                transition={{
                  duration: 2,
                  repeat: isHovering ? Infinity : 0,
                  repeatType: "reverse",
                }}
              ></motion.div>

              {/* Profile border */}
              <div className="absolute inset-1 bg-gradient-to-r from-primary to-secondary rounded-full z-10 overflow-hidden p-1 group-hover:p-1.5 transition-all duration-300">
                <div className="bg-white rounded-full w-full h-full overflow-hidden">
                  <img
                    src="https://opsgvvtoutcxldtxqkls.supabase.co/storage/v1/object/public/portfolio//avatar-min.webp"
                    alt="Abdullah Alioğlu"
                    className="w-full h-full object-cover rounded-full transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
              </div>

              {/* Orbit effect */}
              <div className="absolute inset-0 rounded-full z-0">
                <div className="absolute w-full h-full animate-spin-slow">
                  <div className="absolute top-0 left-1/2 w-4 h-4 bg-white rounded-full shadow-lg transform -translate-x-1/2 -translate-y-1/2"></div>
                </div>
                <div className="absolute w-full h-full animate-spin-slow animation-delay-2000">
                  <div className="absolute bottom-0 left-1/2 w-3 h-3 bg-primary rounded-full shadow-lg transform -translate-x-1/2 translate-y-1/2"></div>
                </div>
                <div className="absolute w-full h-full animate-spin-reverse-slow">
                  <div className="absolute top-1/2 right-0 w-3 h-3 bg-secondary rounded-full shadow-lg transform translate-x-1/2 -translate-y-1/2"></div>
                </div>
              </div>

              {/* Experience badge */}
              <motion.div
                className="absolute -top-4 -right-4 bg-white rounded-xl p-3 shadow-lg z-20"
                animate={{
                  y: isHovering ? -5 : 0,
                  scale: isHovering ? 1.1 : 1,
                }}
                transition={{
                  duration: 0.3,
                }}
              >
                <div className="h-12 w-12 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center text-white text-xl">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Down Indicator - More professional and animated */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden md:flex flex-col items-center"
      >
        <ScrollLink
          to="about"
          spy={true}
          smooth={true}
          offset={-70}
          duration={500}
          className="cursor-pointer flex flex-col items-center bg-white/80 backdrop-blur-sm p-3 rounded-full shadow-md hover:shadow-lg transition-all duration-300 border border-primary/20 hover:border-primary/50"
        >
          <div className="relative">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center">
              <motion.div
                animate={{
                  y: [0, 3, 0],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  />
                </svg>
              </motion.div>
            </div>
            <motion.div
              className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 z-0 opacity-0"
              animate={{
                opacity: [0, 0.5, 0],
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </div>
        </ScrollLink>
      </motion.div>
    </section>
  );
}

export default Home;
